// pages/PublicQuoteView.jsx
import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  FileText,
  CheckCircle,
  Download,
  Clock,
  AlertCircle,
  Loader,
  Building2,
  Mail,
  Phone,
  Globe,
  X,
  Edit3,
  ChevronUp,
  ChevronDown,
  Printer,
  Share2,
  MapPin,
} from "lucide-react";
import apiService from "../../services/api";

/* ─────────────────────────────────────────
   NAV SECTIONS – mirrors the proposal sidebar
───────────────────────────────────────── */
const NAV_SECTIONS = [
  { id: "introduction", label: "Introduction" },
  { id: "about", label: "Why Us" },
  { id: "scope", label: "Scope of Works" },
  { id: "investment", label: "Investment" },
  { id: "order", label: "Order Form" },
  { id: "terms", label: "Terms and Conditions" },
  { id: "contact", label: "Contact Us" },
];

/* ─────────────────────────────────────────
   HELPERS
───────────────────────────────────────── */
const fmt = (amount, currency = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(
    amount || 0,
  );

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

const escapeHtml = (str) =>
  String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const PublicQuoteView = () => {
  const { token } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState("introduction");
  const [showSignModal, setShowSignModal] = useState(false);
  const [signMode, setSignMode] = useState("type"); // "type" | "draw"

  // Signature state
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [typedSig, setTypedSig] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Section refs for scroll spy
  const sectionRefs = useRef({});

  useEffect(() => {
    fetchQuote();
    const start = Date.now();
    const interval = setInterval(() => {
      const seconds = Math.floor((Date.now() - start) / 1000);
      apiService.trackPublicQuoteDuration(token, seconds).catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, [token]);

  /* ──  FIX: Force scroll unlock on mount ── */
  useEffect(() => {
    const prev = {
      htmlOverflow: document.documentElement.style.overflow,
      htmlHeight: document.documentElement.style.height,
      bodyOverflow: document.body.style.overflow,
      bodyHeight: document.body.style.height,
    };

    document.documentElement.style.overflow = "auto";
    document.documentElement.style.height = "auto";
    document.body.style.overflow = "auto";
    document.body.style.height = "auto";

    const rootEl =
      document.getElementById("root") || document.getElementById("__next");
    let prevRoot = null;
    if (rootEl) {
      prevRoot = {
        overflow: rootEl.style.overflow,
        height: rootEl.style.height,
        maxHeight: rootEl.style.maxHeight,
      };
      rootEl.style.overflow = "visible";
      rootEl.style.height = "auto";
      rootEl.style.maxHeight = "none";
    }

    return () => {
      document.documentElement.style.overflow = prev.htmlOverflow;
      document.documentElement.style.height = prev.htmlHeight;
      document.body.style.overflow = prev.bodyOverflow;
      document.body.style.height = prev.bodyHeight;
      if (rootEl && prevRoot) {
        rootEl.style.overflow = prevRoot.overflow;
        rootEl.style.height = prevRoot.height;
        rootEl.style.maxHeight = prevRoot.maxHeight;
      }
    };
  }, []);

  // Scroll spy
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" },
    );
    Object.values(sectionRefs.current).forEach(
      (el) => el && observer.observe(el),
    );
    return () => observer.disconnect();
  }, [quote]);

  const fetchQuote = async () => {
    try {
      setLoading(true);
      const res = await apiService.getPublicQuote(token);
      if (res.success) {
        setQuote(res.data);
        setSignerName(res.data.customerName || "");
        setSignerEmail(res.data.customerEmail || "");
      } else {
        setError(res.message || "Quote not found");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Unable to load quote");
    } finally {
      setLoading(false);
    }
  };

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Canvas helpers
  const initCanvas = (canvas) => {
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };
  const startDraw = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX ?? e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY ?? e.touches?.[0]?.clientY) - rect.top;
    canvas.getContext("2d").beginPath();
    canvas.getContext("2d").moveTo(x, y);
    setDrawing(true);
  };
  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX ?? e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY ?? e.touches?.[0]?.clientY) - rect.top;
    const ctx = canvas.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };
  const stopDraw = () => setDrawing(false);
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleSign = async () => {
    const ready =
      signerName &&
      signerEmail &&
      consent &&
      (signMode === "type" ? typedSig.trim() : hasDrawn);
    if (!ready) {
      alert("Please complete all required fields and accept the terms.");
      return;
    }
    try {
      setSubmitting(true);
      let signatureImage;
      if (signMode === "draw") {
        signatureImage = canvasRef.current.toDataURL("image/png");
      } else {
        const oc = document.createElement("canvas");
        oc.width = 480;
        oc.height = 120;
        const ctx = oc.getContext("2d");
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, 480, 120);
        ctx.font = "italic 52px Georgia, serif";
        ctx.fillStyle = "#0f172a";
        ctx.fillText(typedSig, 20, 80);
        signatureImage = oc.toDataURL("image/png");
      }
      const res = await apiService.signPublicQuote(token, {
        signerName,
        signerEmail,
        signerTitle,
        signatureImage,
        signatureMethod: signMode === "draw" ? "drawn" : "typed",
        consentAccepted: true,
      });
      if (res.success) {
        setShowSignModal(false);
        fetchQuote();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Signing failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ─────────────────────────────────────────
     GENERATE CONTRACT PDF
     Opens a new window with a properly-formatted
     contract document and triggers the browser's
     print-to-PDF dialog.
  ───────────────────────────────────────── */
  const generatePDF = () => {
    if (!quote) return;

    const printWindow = window.open("", "_blank", "width=900,height=1100");
    if (!printWindow) {
      alert("Please allow pop-ups to download the contract PDF.");
      return;
    }

    const currency = quote.currency || "GBP";
    const today = fmtDate(new Date());
    const isSigned = ["signed", "accepted"].includes(quote.status);

    // Build scope rows
    const scopeRows = (quote.lineItems || [])
      .map((li, i) => {
        const qty = parseInt(li.quantity) || 1;
        const unit = parseFloat(li.unitPrice) || 0;
        const disc = parseFloat(li.discount) || 0;
        const tax = parseFloat(li.taxRate) || 0;
        const lineSub = qty * unit - disc;
        const lineTotal =
          parseFloat(li.totalPrice) || lineSub * (1 + tax / 100);
        const isRecurring =
          li.billingCycle &&
          li.billingCycle !== "one_time" &&
          li.billingCycle !== "";
        const cycleLabel = isRecurring
          ? ` (${li.billingCycle.replace(/_/g, " ")})`
          : "";
        return `
          <tr>
            <td class="num-col">${i + 1}.</td>
            <td>
              <strong>${escapeHtml(li.name)}</strong>${cycleLabel ? `<span class="meta">${escapeHtml(cycleLabel)}</span>` : ""}
              ${li.description ? `<div class="desc">${escapeHtml(li.description)}</div>` : ""}
              ${li.termMonths ? `<div class="meta">Term: ${li.termMonths} months</div>` : ""}
            </td>
            <td class="right">${qty}</td>
            <td class="right">${fmt(unit, currency)}</td>
            <td class="right"><strong>${fmt(lineTotal, currency)}</strong></td>
          </tr>
        `;
      })
      .join("");

    const totalsRows = `
      ${
        parseFloat(quote.subtotal) !== parseFloat(quote.totalAmount)
          ? `<tr><td colspan="4" class="right label">Subtotal</td><td class="right">${fmt(quote.subtotal, currency)}</td></tr>`
          : ""
      }
      ${
        parseFloat(quote.discountAmount) > 0
          ? `<tr><td colspan="4" class="right label">Discount${
              quote.discountType === "percentage"
                ? ` (${quote.discountAmount}%)`
                : ""
            }</td><td class="right">−${
              quote.discountType === "percentage"
                ? `${quote.discountAmount}%`
                : fmt(quote.discountAmount, currency)
            }</td></tr>`
          : ""
      }
      ${
        parseFloat(quote.taxAmount) > 0
          ? `<tr><td colspan="4" class="right label">VAT / Tax</td><td class="right">${fmt(quote.taxAmount, currency)}</td></tr>`
          : ""
      }
      <tr class="grand"><td colspan="4" class="right label">TOTAL CONTRACT VALUE</td><td class="right">${fmt(quote.totalAmount, currency)}</td></tr>
    `;

    // Signature block
    const signatureBlock =
      isSigned && quote.signature
        ? `
        <div class="sig-grid">
          <div class="sig-col">
            <div class="sig-label">FOR AND ON BEHALF OF THE CLIENT</div>
            <div class="sig-img-wrap">
              <img src="${quote.signature.signatureImage}" alt="Signature" class="sig-img" />
            </div>
            <div class="sig-line"></div>
            <div class="sig-detail"><strong>${escapeHtml(quote.signature.signerName)}</strong></div>
            ${quote.signature.signerTitle ? `<div class="sig-detail">${escapeHtml(quote.signature.signerTitle)}</div>` : ""}
            <div class="sig-detail">${escapeHtml(quote.signature.signerEmail || "")}</div>
            <div class="sig-detail">Date: ${fmtDate(quote.signature.signedAt)}</div>
          </div>
          <div class="sig-col">
            <div class="sig-label">FOR AND ON BEHALF OF THE PROVIDER</div>
            <div class="sig-img-wrap"></div>
            <div class="sig-line"></div>
            <div class="sig-detail"><strong>${escapeHtml(quote.company?.name || "")}</strong></div>
            <div class="sig-detail">Authorised Signatory</div>
            <div class="sig-detail">Date: _____________________</div>
          </div>
        </div>
      `
        : `
        <div class="sig-grid">
          <div class="sig-col">
            <div class="sig-label">FOR AND ON BEHALF OF THE CLIENT</div>
            <div class="sig-img-wrap"></div>
            <div class="sig-line"></div>
            <div class="sig-detail">Name: _____________________________</div>
            <div class="sig-detail">Title: ______________________________</div>
            <div class="sig-detail">Date: ______________________________</div>
          </div>
          <div class="sig-col">
            <div class="sig-label">FOR AND ON BEHALF OF THE PROVIDER</div>
            <div class="sig-img-wrap"></div>
            <div class="sig-line"></div>
            <div class="sig-detail">Name: _____________________________</div>
            <div class="sig-detail">Title: ______________________________</div>
            <div class="sig-detail">Date: ______________________________</div>
          </div>
        </div>
      `;

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Services Agreement — ${escapeHtml(quote.quoteNumber)}</title>
  <style>
    @page {
      size: A4;
      margin: 22mm 20mm 25mm 20mm;
      @bottom-center {
        content: "Page " counter(page) " of " counter(pages);
        font-family: Georgia, 'Times New Roman', serif;
        font-size: 9pt;
        color: #555;
      }
    }

    * { box-sizing: border-box; }

    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #1a1a1a;
      font-family: Georgia, 'Times New Roman', Times, serif;
      font-size: 11pt;
      line-height: 1.65;
    }

    .page {
      max-width: 170mm;
      margin: 0 auto;
      padding: 0;
    }

    /* ── COVER / TITLE ── */
    .cover {
      text-align: center;
      padding: 40mm 0 20mm 0;
      page-break-after: always;
    }
    .cover .brand {
      font-size: 11pt;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      color: #666;
      margin-bottom: 4mm;
    }
    .cover .agreement-type {
      font-size: 10pt;
      letter-spacing: 0.3em;
      text-transform: uppercase;
      color: #888;
      margin-bottom: 30mm;
    }
    .cover h1 {
      font-size: 32pt;
      font-weight: 400;
      letter-spacing: 0.05em;
      margin: 0 0 4mm 0;
      line-height: 1.15;
    }
    .cover .divider {
      width: 40mm;
      height: 1px;
      background: #1a1a1a;
      margin: 10mm auto;
    }
    .cover .subtitle {
      font-size: 13pt;
      font-style: italic;
      color: #444;
      margin-bottom: 20mm;
    }
    .cover .parties {
      font-size: 11pt;
      line-height: 2;
      margin-top: 30mm;
    }
    .cover .parties strong {
      display: block;
      font-size: 10pt;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: #888;
      font-weight: 400;
      margin-bottom: 2mm;
    }
    .cover .parties .party-name {
      font-size: 14pt;
      font-weight: 600;
    }
    .cover .between {
      font-size: 10pt;
      letter-spacing: 0.3em;
      color: #888;
      margin: 8mm 0;
    }
    .cover .meta {
      margin-top: 25mm;
      font-size: 10pt;
      color: #555;
      line-height: 1.8;
    }
    .cover .meta div { display: inline-block; margin: 0 6mm; }

    /* ── BODY SECTIONS ── */
    h2.section-title {
      font-size: 12pt;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      border-bottom: 1.5pt solid #1a1a1a;
      padding-bottom: 2mm;
      margin: 8mm 0 4mm 0;
      page-break-after: avoid;
    }

    h3.sub-title {
      font-size: 11pt;
      font-weight: 700;
      margin: 5mm 0 2mm 0;
      page-break-after: avoid;
    }

    p { margin: 0 0 3mm 0; text-align: justify; }

    .recital { text-align: justify; margin-bottom: 3mm; }

    /* ── PARTIES BOX ── */
    .parties-box {
      border: 0.75pt solid #1a1a1a;
      padding: 5mm 6mm;
      margin: 6mm 0;
    }
    .parties-box .party { margin-bottom: 4mm; }
    .parties-box .party:last-child { margin-bottom: 0; }
    .parties-box .party-role {
      font-size: 9pt;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      color: #666;
      margin-bottom: 1mm;
    }
    .parties-box .party-name {
      font-weight: 700;
      font-size: 12pt;
    }
    .parties-box .party-detail {
      font-size: 10pt;
      color: #333;
    }

    /* ── CLAUSE LIST ── */
    ol.clauses {
      padding-left: 0;
      counter-reset: clause;
      list-style: none;
    }
    ol.clauses > li {
      counter-increment: clause;
      margin-bottom: 5mm;
      padding-left: 12mm;
      position: relative;
    }
    ol.clauses > li::before {
      content: counter(clause) ".";
      position: absolute;
      left: 0;
      top: 0;
      font-weight: 700;
      width: 10mm;
    }
    ol.sub-clauses {
      padding-left: 0;
      list-style: none;
      counter-reset: sub;
      margin-top: 2mm;
    }
    ol.sub-clauses > li {
      counter-increment: sub;
      margin-bottom: 2mm;
      padding-left: 10mm;
      position: relative;
    }
    ol.sub-clauses > li::before {
      content: counter(clause) "." counter(sub);
      position: absolute;
      left: 0;
      top: 0;
      font-weight: 600;
      width: 9mm;
    }

    /* ── SCOPE TABLE ── */
    table.scope {
      width: 100%;
      border-collapse: collapse;
      margin: 4mm 0 6mm 0;
      font-size: 10pt;
    }
    table.scope th {
      background: #1a1a1a;
      color: #fff;
      font-weight: 700;
      font-size: 9pt;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      text-align: left;
      padding: 3mm 3mm;
    }
    table.scope th.right { text-align: right; }
    table.scope td {
      padding: 3mm 3mm;
      border-bottom: 0.5pt solid #ccc;
      vertical-align: top;
    }
    table.scope td.num-col {
      width: 8mm;
      color: #888;
      font-size: 9pt;
    }
    table.scope td.right { text-align: right; white-space: nowrap; }
    table.scope td .meta {
      display: block;
      font-size: 9pt;
      color: #777;
      font-style: italic;
      margin-top: 1mm;
    }
    table.scope td .desc {
      font-size: 9.5pt;
      color: #555;
      margin-top: 1mm;
      line-height: 1.5;
    }
    table.scope tr.grand td {
      background: #f4f3ee;
      border-top: 1.5pt solid #1a1a1a;
      border-bottom: none;
      font-size: 11pt;
      font-weight: 700;
      letter-spacing: 0.04em;
      padding-top: 4mm;
      padding-bottom: 4mm;
    }
    table.scope tr td.label {
      font-weight: 600;
      color: #444;
    }

    /* ── KEY / VALUE GRID (doc control) ── */
    .kv-table {
      width: 100%;
      border-collapse: collapse;
      margin: 3mm 0 6mm 0;
      font-size: 10pt;
    }
    .kv-table td {
      padding: 2mm 3mm;
      border-bottom: 0.25pt solid #ccc;
      vertical-align: top;
    }
    .kv-table td.k {
      width: 45mm;
      color: #666;
      font-size: 9.5pt;
    }
    .kv-table td.v {
      font-weight: 600;
    }

    /* ── TERMS BODY ── */
    .terms-body {
      white-space: pre-wrap;
      font-size: 10pt;
      line-height: 1.7;
      text-align: justify;
    }

    /* ── PAYMENT BLOCK ── */
    .callout {
      background: #f4f3ee;
      border-left: 3pt solid #1a1a1a;
      padding: 4mm 5mm;
      margin: 4mm 0;
      font-size: 10pt;
    }

    /* ── SIGNATURE BLOCK ── */
    .signature-section {
      page-break-inside: avoid;
      margin-top: 10mm;
    }
    .sig-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12mm;
      margin-top: 8mm;
    }
    .sig-col { page-break-inside: avoid; }
    .sig-label {
      font-size: 9pt;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #666;
      margin-bottom: 3mm;
      font-weight: 600;
    }
    .sig-img-wrap {
      min-height: 20mm;
      border-bottom: 0.75pt solid #1a1a1a;
      padding-bottom: 2mm;
      display: flex;
      align-items: flex-end;
    }
    .sig-img { max-height: 18mm; max-width: 100%; }
    .sig-line { height: 0; }
    .sig-detail {
      font-size: 10pt;
      color: #333;
      margin-top: 1.5mm;
      line-height: 1.5;
    }

    .sign-notice {
      background: #f0fdf4;
      border: 0.5pt solid #86efac;
      padding: 4mm 5mm;
      margin: 6mm 0;
      font-size: 10pt;
      color: #166534;
    }

    .footer-note {
      text-align: center;
      margin-top: 10mm;
      font-size: 9pt;
      color: #888;
      font-style: italic;
    }

    .page-break { page-break-before: always; }
  </style>
</head>
<body>
  <div class="page">

    <!-- ─── COVER PAGE ─── -->
    <div class="cover">
      <div class="brand">${escapeHtml(quote.company?.name || "")}</div>
      <div class="agreement-type">Services Agreement</div>

      <h1>${escapeHtml(quote.title)}</h1>

      <div class="divider"></div>

      <div class="subtitle">Contract Reference #${escapeHtml(quote.quoteNumber)}</div>

      <div class="parties">
        <div>
          <strong>Between</strong>
          <div class="party-name">${escapeHtml(quote.company?.name || "—")}</div>
          ${quote.company?.registrationNumber ? `<div style="font-size:10pt;color:#666;">Company No. ${escapeHtml(quote.company.registrationNumber)}</div>` : ""}
        </div>

        <div class="between">— AND —</div>

        <div>
          <strong>And</strong>
          <div class="party-name">${escapeHtml(quote.customerCompany || quote.customerName || "The Client")}</div>
          ${quote.customerName && quote.customerCompany ? `<div style="font-size:10pt;color:#666;">c/o ${escapeHtml(quote.customerName)}</div>` : ""}
        </div>
      </div>

      <div class="meta">
        <div><strong>Date Issued:</strong> ${fmtDate(quote.createdAt)}</div>
        ${quote.validUntil ? `<div><strong>Valid Until:</strong> ${fmtDate(quote.validUntil)}</div>` : ""}
      </div>
    </div>

    <!-- ─── AGREEMENT BODY ─── -->

    <h2 class="section-title">Document Control</h2>
    <table class="kv-table">
      <tr><td class="k">Contract Reference</td><td class="v">#${escapeHtml(quote.quoteNumber)}</td></tr>
      <tr><td class="k">Date of Issue</td><td class="v">${fmtDate(quote.createdAt)}</td></tr>
      ${quote.validUntil ? `<tr><td class="k">Valid Until</td><td class="v">${fmtDate(quote.validUntil)}</td></tr>` : ""}
      <tr><td class="k">Prepared By</td><td class="v">${escapeHtml(quote.company?.name || "—")}</td></tr>
      <tr><td class="k">Prepared For</td><td class="v">${escapeHtml(quote.customerCompany || quote.customerName || "—")}</td></tr>
      ${quote.customerEmail ? `<tr><td class="k">Client Contact</td><td class="v">${escapeHtml(quote.customerEmail)}</td></tr>` : ""}
      <tr><td class="k">Governing Law</td><td class="v">England &amp; Wales</td></tr>
    </table>

    <h2 class="section-title">Parties to the Agreement</h2>
    <div class="parties-box">
      <div class="party">
        <div class="party-role">The Provider</div>
        <div class="party-name">${escapeHtml(quote.company?.name || "—")}</div>
        ${quote.company?.registrationNumber ? `<div class="party-detail">Company Registration No. ${escapeHtml(quote.company.registrationNumber)}</div>` : ""}
        ${quote.company?.address ? `<div class="party-detail">${escapeHtml(quote.company.address)}</div>` : ""}
        ${quote.company?.email ? `<div class="party-detail">Email: ${escapeHtml(quote.company.email)}</div>` : ""}
        ${quote.company?.phone ? `<div class="party-detail">Telephone: ${escapeHtml(quote.company.phone)}</div>` : ""}
      </div>
      <div class="party">
        <div class="party-role">The Client</div>
        <div class="party-name">${escapeHtml(quote.customerCompany || quote.customerName || "—")}</div>
        ${quote.customerName && quote.customerCompany ? `<div class="party-detail">Authorised Contact: ${escapeHtml(quote.customerName)}</div>` : ""}
        ${quote.customerEmail ? `<div class="party-detail">Email: ${escapeHtml(quote.customerEmail)}</div>` : ""}
      </div>
    </div>

    <h2 class="section-title">Recitals</h2>
    <p class="recital"><strong>WHEREAS</strong> the Provider is engaged in the business of providing professional services as further described in this Agreement;</p>
    <p class="recital"><strong>WHEREAS</strong> the Client wishes to engage the Provider to supply such services on the terms set out herein; and</p>
    <p class="recital"><strong>WHEREAS</strong> the Parties have agreed to enter into this Agreement to formalise their respective rights and obligations.</p>
    <p class="recital"><strong>NOW, THEREFORE</strong>, in consideration of the mutual covenants and agreements contained herein, and for other good and valuable consideration, the Parties agree as follows:</p>

    <ol class="clauses">
      <li>
        <h3 class="sub-title" style="display:inline;">Scope of Services</h3>
        ${quote.scopeIntro ? `<p style="margin-top:2mm;">${escapeHtml(quote.scopeIntro)}</p>` : ""}
        <p style="margin-top:2mm;">The Provider shall supply, and the Client shall accept, the services and deliverables set out in the table below (collectively, the "<strong>Services</strong>"):</p>
        <table class="scope">
          <thead>
            <tr>
              <th style="width:6mm;"></th>
              <th>Description of Services</th>
              <th class="right" style="width:14mm;">Qty</th>
              <th class="right" style="width:26mm;">Unit Price</th>
              <th class="right" style="width:28mm;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${scopeRows}
            ${totalsRows}
          </tbody>
        </table>
      </li>

      <li>
        <h3 class="sub-title" style="display:inline;">Fees and Consideration</h3>
        <p style="margin-top:2mm;">In consideration of the Services to be provided under this Agreement, the Client shall pay the Provider the total sum of <strong>${fmt(quote.totalAmount, currency)}</strong> (the "<strong>Fees</strong>"), calculated in accordance with the schedule set out in Clause 1 above.</p>
        ${parseFloat(quote.monthlyRecurring) > 0 ? `<p>Recurring charges amount to <strong>${fmt(quote.monthlyRecurring, currency)} per month</strong> and shall continue for the duration of the applicable term unless terminated in accordance with this Agreement.</p>` : ""}
        <p>All Fees are exclusive of VAT unless expressly stated, which shall be payable by the Client at the prevailing rate.</p>
      </li>

      <li>
        <h3 class="sub-title" style="display:inline;">Payment Terms</h3>
        ${
          quote.paymentTerms
            ? `<div class="callout">${escapeHtml(quote.paymentTerms)}</div>`
            : `<p style="margin-top:2mm;">Invoices shall be raised in accordance with the agreed schedule and payable within thirty (30) days of the invoice date. Any sums outstanding beyond the due date may attract interest at the statutory rate under the Late Payment of Commercial Debts (Interest) Act 1998.</p>`
        }
      </li>

      <li>
        <h3 class="sub-title" style="display:inline;">Term and Commencement</h3>
        <p style="margin-top:2mm;">This Agreement shall take effect from the date of signature by both Parties (the "<strong>Effective Date</strong>") and shall continue in force until the Services have been fully performed or until terminated in accordance with its terms.</p>
      </li>

      <li>
        <h3 class="sub-title" style="display:inline;">Validity of Offer</h3>
        <p style="margin-top:2mm;">The commercial terms set out in this Agreement shall remain open for acceptance by the Client ${quote.validUntil ? `until <strong>${fmtDate(quote.validUntil)}</strong>` : "for a period of thirty (30) days from the date of issue"}, after which the Provider reserves the right to revise the terms or withdraw the offer.</p>
      </li>

      <li>
        <h3 class="sub-title" style="display:inline;">Acceptance and Execution</h3>
        <p style="margin-top:2mm;">The Client accepts the terms of this Agreement by executing the signature block at the foot of this document. An electronic signature shall carry the same legal force and effect as a manuscript signature and shall be deemed valid evidence of acceptance.</p>
      </li>

      <li>
        <h3 class="sub-title" style="display:inline;">General Provisions</h3>
        <ol class="sub-clauses">
          <li>This Agreement, together with any annexes and the Terms and Conditions set out below, constitutes the entire agreement between the Parties and supersedes any prior arrangements, whether written or oral.</li>
          <li>No variation of this Agreement shall be effective unless agreed in writing by both Parties.</li>
          <li>This Agreement shall be governed by and construed in accordance with the laws of England and Wales, and the Parties submit to the exclusive jurisdiction of its courts.</li>
        </ol>
      </li>
    </ol>

    ${
      quote.termsAndConditions
        ? `
      <div class="page-break"></div>
      <h2 class="section-title">Terms &amp; Conditions</h2>
      <div class="terms-body">${escapeHtml(quote.termsAndConditions)}</div>
    `
        : ""
    }

    <!-- ─── SIGNATURES ─── -->
    <div class="page-break"></div>
    <h2 class="section-title">Execution</h2>
    <p>IN WITNESS WHEREOF, the Parties have executed this Agreement as of the date first written below.</p>

    ${
      isSigned
        ? `<div class="sign-notice"><strong>✓ This Agreement was executed electronically on ${fmtDate(quote.signedAt)}.</strong></div>`
        : ""
    }

    <div class="signature-section">
      ${signatureBlock}
    </div>

    <div class="footer-note">
      — End of Agreement —<br/>
      Document generated on ${today} · Reference #${escapeHtml(quote.quoteNumber)}
    </div>

  </div>

  <script>
    window.addEventListener('load', function() {
      setTimeout(function() {
        window.focus();
        window.print();
      }, 300);
    });
    window.addEventListener('afterprint', function() {
      setTimeout(function() { window.close(); }, 100);
    });
  </script>
</body>
</html>`;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  /* ── States ── */
  if (loading)
    return (
      <div style={styles.centreScreen}>
        <Loader
          style={{ animation: "spin 1s linear infinite", color: "#3b82f6" }}
          size={42}
        />
      </div>
    );

  if (error || !quote)
    return (
      <div style={styles.centreScreen}>
        <div style={styles.errorCard}>
          <AlertCircle size={48} color="#ef4444" style={{ marginBottom: 16 }} />
          <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
            Quote Unavailable
          </h1>
          <p style={{ color: "#64748b" }}>
            {error || "This quote cannot be found."}
          </p>
        </div>
      </div>
    );

  const isSigned = ["signed", "accepted"].includes(quote.status);
  const isExpired = quote.status === "expired";
  const currency = quote.currency || "GBP";

  /* ── Determine which nav sections to show ── */
  const visibleSections = NAV_SECTIONS.filter((s) => {
    if (s.id === "about" && !quote.aboutSection) return false;
    if (s.id === "terms" && !quote.termsAndConditions) return false;
    if (s.id === "contact" && !quote.company?.email && !quote.company?.phone)
      return false;
    return true;
  });

  /* ─────────────────────────────────────
     RENDER
  ───────────────────────────────────── */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

        html, body {
          overflow-x: hidden !important;
          overflow-y: auto !important;
          height: auto !important;
          min-height: 100%;
          margin: 0;
          padding: 0;
        }
        #root, #__next {
          overflow: visible !important;
          height: auto !important;
          max-height: none !important;
          min-height: 100vh;
        }

        * { box-sizing: border-box; }

        html { scroll-behavior: smooth; }

        body { font-family: 'DM Sans', sans-serif; }

        .pqv-root {
          background: #f5f4f0;
          color: #1a1a1a;
          font-family: 'DM Sans', sans-serif;
          min-height: 100vh;
          width: 100%;
          position: relative;
          overflow: visible;
        }

        .pqv-sidebar {
          position: fixed;
          top: 0; left: 0;
          width: 260px;
          height: 100vh;
          background: #fff;
          border-right: 1px solid #e5e2da;
          display: flex;
          flex-direction: column;
          z-index: 100;
          overflow-y: auto;
        }

        .pqv-logo-area {
          padding: 28px 24px 20px;
          border-bottom: 1px solid #e5e2da;
        }

        .pqv-logo-area img { max-height: 48px; max-width: 180px; object-fit: contain; }

        .pqv-logo-placeholder {
          width: 48px; height: 48px;
          background: #1a1a1a;
          border-radius: 6px;
          display: flex; align-items: center; justify-content: center;
          color: #fff;
        }

        .pqv-company-sub {
          font-size: 10px;
          color: #9e9b94;
          margin-top: 6px;
          line-height: 1.4;
        }

        .pqv-nav { padding: 20px 0; flex: 1; }

        .pqv-nav-item {
          display: block;
          width: 100%;
          text-align: left;
          background: none;
          border: none;
          padding: 11px 24px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13.5px;
          font-weight: 400;
          color: #6b6860;
          cursor: pointer;
          transition: color 0.2s ease-in-out, background 0.2s ease-in-out, border-color 0.2s ease-in-out;
          border-left: 3px solid transparent;
        }

        .pqv-nav-item:hover { color: #1a1a1a; background: #f5f4f0; }

        .pqv-nav-item.active {
          color: #1a1a1a;
          font-weight: 600;
          border-left-color: #3b82f6;
          background: #faf9f6;
        }

        .pqv-sidebar-actions {
          padding: 16px 20px;
          border-top: 1px solid #e5e2da;
          display: flex;
          gap: 8px;
        }

        .pqv-btn-icon {
          flex: 1;
          display: flex; align-items: center; justify-content: center;
          gap: 6px;
          padding: 9px 8px;
          border: 1px solid #e5e2da;
          border-radius: 6px;
          background: white;
          font-size: 12px;
          color: #6b6860;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          transition: all 0.15s;
        }
        .pqv-btn-icon:hover { background: #f0f7ff; color: #1a1a1a; border-color: #bfdbfe; }

        .pqv-btn-sign {
          width: 100%;
          padding: 12px;
          background: #1a1a1a;
          color: white;
          border: none;
          border-radius: 6px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13.5px;
          font-weight: 600;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: background 0.15s;
          margin-top: 8px;
        }
        .pqv-btn-sign:hover { background: #2563eb; }

        .pqv-main {
          margin-left: 260px;
          min-height: 100vh;
        }

        .pqv-section {
          padding: 72px 80px;
          border-bottom: 1px solid #e5e2da;
          transition: background 0.3s ease;
        }

        .pqv-section:last-child { border-bottom: none; }

        .pqv-section-light { background: #fff; }
        .pqv-section-cream { background: #f5f4f0; }
        .pqv-section-dark  { background: #1a1a1a; color: #f5f4f0; }

        .pqv-eyebrow {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #3b82f6;
          margin-bottom: 14px;
        }

        .pqv-display {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(36px, 4vw, 58px);
          font-weight: 600;
          line-height: 1.15;
          color: #1a1a1a;
          margin-bottom: 28px;
        }

        .pqv-display-light {
          font-family: 'Cormorant Garamond', serif;
          font-size: clamp(36px, 4vw, 58px);
          font-weight: 600;
          line-height: 1.15;
          color: #f5f4f0;
          margin-bottom: 28px;
        }

        .pqv-body {
          font-size: 15px;
          line-height: 1.75;
          color: #4a4744;
          max-width: 680px;
        }

        .pqv-body + .pqv-body { margin-top: 16px; }

        .pqv-doc-control {
          margin-top: 56px;
          border: 1px solid #e5e2da;
          border-radius: 2px;
          overflow: hidden;
          max-width: 620px;
        }

        .pqv-doc-control-row {
          display: grid;
          grid-template-columns: 180px 1fr;
          border-bottom: 1px solid #e5e2da;
        }
        .pqv-doc-control-row:last-child { border-bottom: none; }

        .pqv-dc-label {
          padding: 12px 16px;
          font-size: 12.5px;
          color: #6b6860;
          background: #faf9f6;
          border-right: 1px solid #e5e2da;
        }

        .pqv-dc-value {
          padding: 12px 16px;
          font-size: 13px;
          font-weight: 500;
          color: #1a1a1a;
        }

        .pqv-status-bar {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 80px;
          font-size: 13.5px;
          font-weight: 500;
        }
        .pqv-status-bar.signed { background: #f0fdf4; color: #166534; border-bottom: 1px solid #bbf7d0; }
        .pqv-status-bar.expired { background: #fef2f2; color: #991b1b; border-bottom: 1px solid #fecaca; }

        .pqv-scope-item {
          border-bottom: 1px solid #e5e2da;
          padding: 28px 0;
        }
        .pqv-scope-item:first-child { padding-top: 0; }
        .pqv-scope-item:last-child  { border-bottom: none; }

        .pqv-scope-title {
          font-size: 15px;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 6px;
        }

        .pqv-scope-desc {
          font-size: 13.5px;
          line-height: 1.7;
          color: #6b6860;
        }

        .pqv-inv-card {
          background: white;
          border: 1px solid #e5e2da;
          border-radius: 2px;
          overflow: hidden;
          margin-bottom: 20px;
        }

        .pqv-inv-card-header {
          background: #1a1a1a;
          color: white;
          padding: 14px 20px;
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 0.02em;
        }

        .pqv-inv-card-body { padding: 0; }

        .pqv-line-item {
          display: grid;
          grid-template-columns: 1fr 80px 120px 120px;
          gap: 0;
          border-bottom: 1px solid #f0ede8;
          align-items: start;
          transition: background 0.15s ease;
        }
        .pqv-line-item:hover { background: #f8faff; }
        .pqv-line-item:last-child { border-bottom: none; }

        .pqv-li-cell {
          padding: 14px 20px;
          font-size: 13.5px;
        }
        .pqv-li-name { font-weight: 500; color: #1a1a1a; }
        .pqv-li-desc { font-size: 12px; color: #9e9b94; margin-top: 3px; }
        .pqv-li-badge {
          display: inline-block;
          font-size: 10px; font-weight: 600;
          padding: 2px 7px;
          background: #eff6ff;
          color: #1d4ed8;
          border: 1px solid #bfdbfe;
          border-radius: 3px;
          margin-top: 4px;
          text-transform: capitalize;
          letter-spacing: 0.02em;
        }
        .pqv-li-num { text-align: right; color: #4a4744; }
        .pqv-li-total { text-align: right; font-weight: 600; color: #1a1a1a; }

        .pqv-col-header {
          display: grid;
          grid-template-columns: 1fr 80px 120px 120px;
          border-bottom: 2px solid #1a1a1a;
          padding: 10px 20px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #6b6860;
        }
        .pqv-col-header span:not(:first-child) { text-align: right; }

        .pqv-totals {
          border-top: 2px solid #1a1a1a;
          padding: 16px 20px;
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 6px;
        }
        .pqv-total-row {
          display: flex; gap: 80px;
          font-size: 13.5px; color: #4a4744;
        }
        .pqv-total-row.grand {
          font-size: 17px; font-weight: 700; color: #1a1a1a;
          margin-top: 4px; padding-top: 8px;
          border-top: 1px solid #e5e2da;
        }
        .pqv-total-row span:last-child { min-width: 120px; text-align: right; }

        .pqv-payment-block {
          background: #faf9f6;
          border: 1px solid #e5e2da;
          border-left: 4px solid #3b82f6;
          padding: 20px 24px;
          margin-top: 24px;
          border-radius: 0 2px 2px 0;
        }
        .pqv-payment-label {
          font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #3b82f6; margin-bottom: 6px;
        }
        .pqv-payment-text { font-size: 13.5px; line-height: 1.7; color: #4a4744; }

        .pqv-order-steps {
          counter-reset: step;
          list-style: none;
          max-width: 560px;
          margin: 32px 0;
          padding: 0;
        }
        .pqv-order-steps li {
          counter-increment: step;
          display: flex; gap: 16px;
          margin-bottom: 18px;
          font-size: 14px; line-height: 1.65; color: #4a4744;
        }
        .pqv-order-steps li::before {
          content: counter(step);
          flex-shrink: 0;
          width: 26px; height: 26px;
          background: #1a1a1a; color: white;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 700;
          margin-top: 1px;
        }

        .pqv-sig-box {
          background: #1a1a1a;
          border-radius: 4px;
          padding: 40px;
          max-width: 640px;
          margin-top: 32px;
          color: #f5f4f0;
        }

        .pqv-sig-consent {
          font-size: 13px;
          line-height: 1.7;
          color: #9e9b94;
          margin-bottom: 28px;
        }

        .pqv-sig-tabs {
          display: flex; gap: 2px;
          margin-bottom: 24px;
          border-bottom: 1px solid #2e2e2e;
        }
        .pqv-sig-tab {
          background: none; border: none;
          padding: 10px 20px;
          font-family: 'DM Sans', sans-serif;
          font-size: 13px; font-weight: 500;
          color: #6b6860; cursor: pointer;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          transition: all 0.15s;
          text-transform: uppercase; letter-spacing: 0.06em;
        }
        .pqv-sig-tab.active { color: #3b82f6; border-bottom-color: #3b82f6; }

        .pqv-sig-input {
          width: 100%; padding: 12px 14px;
          background: #2a2a2a; border: 1px solid #3a3a3a;
          border-radius: 4px; color: #f5f4f0;
          font-family: 'DM Sans', sans-serif; font-size: 14px;
          outline: none; transition: border-color 0.15s;
          margin-bottom: 12px;
        }
        .pqv-sig-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.15); }
        .pqv-sig-input::placeholder { color: #4a4744; }

        .pqv-sig-label {
          font-size: 11px; font-weight: 600; letter-spacing: 0.07em;
          text-transform: uppercase; color: #6b6860; margin-bottom: 6px;
          display: block;
        }

        .pqv-sig-type-field {
          width: 100%;
          padding: 16px;
          background: white;
          border-radius: 4px;
          font-family: Georgia, serif;
          font-style: italic;
          font-size: 36px;
          color: #1a1a1a;
          min-height: 80px;
          display: flex; align-items: center;
          letter-spacing: 0.01em;
          border: 2px solid #3a3a3a;
        }

        .pqv-sig-type-input {
          width: 100%; padding: 12px 14px;
          background: #2a2a2a; border: 1px solid #3a3a3a;
          border-radius: 4px; color: #f5f4f0;
          font-family: 'DM Sans', sans-serif; font-size: 14px;
          outline: none; transition: border-color 0.15s;
        }
        .pqv-sig-type-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.15); }
        .pqv-sig-type-input::placeholder { color: #4a4744; }

        .pqv-sig-canvas-wrap {
          border: 2px dashed #3a3a3a;
          border-radius: 4px;
          background: white;
          position: relative;
        }
        .pqv-sig-canvas-wrap canvas { display: block; width: 100%; height: 130px; cursor: crosshair; touch-action: none; }

        .pqv-sig-clear {
          background: none; border: none;
          font-size: 12px; color: #6b6860;
          cursor: pointer; margin-top: 6px;
          font-family: 'DM Sans', sans-serif;
          padding: 0;
        }
        .pqv-sig-clear:hover { color: #3b82f6; }

        .pqv-consent-row {
          display: flex; gap: 12px; align-items: flex-start;
          margin: 20px 0;
          font-size: 13px; color: #9e9b94; line-height: 1.6;
        }
        .pqv-consent-row input[type="checkbox"] { margin-top: 2px; flex-shrink: 0; accent-color: #3b82f6; width: 15px; height: 15px; cursor: pointer; }

        .pqv-accept-btn {
          width: 100%;
          padding: 15px;
          background: #3b82f6;
          color: #ffffff;
          border: none;
          border-radius: 4px;
          font-family: 'DM Sans', sans-serif;
          font-size: 14px; font-weight: 700;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          transition: background 0.2s ease-in-out, transform 0.1s ease;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }
        .pqv-accept-btn:hover { background: #2563eb; transform: translateY(-1px); }
        .pqv-accept-btn:active { transform: translateY(0); }
        .pqv-accept-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .pqv-signed-display {
          background: #f0fdf4; border: 1px solid #bbf7d0;
          border-radius: 4px; padding: 24px;
          display: flex; gap: 20px; align-items: flex-start;
          max-width: 560px; margin-top: 32px;
        }
        .pqv-signed-img { max-height: 80px; border-radius: 4px; background: white; padding: 8px; border: 1px solid #bbf7d0; }

        .pqv-terms-body {
          font-size: 13.5px; line-height: 1.8; color: #4a4744;
          white-space: pre-wrap; max-width: 760px;
          columns: 1;
        }

        .pqv-contact-item {
          display: flex; align-items: center; gap: 12px;
          font-size: 15px; color: #f5f4f0;
          margin-bottom: 16px;
        }
        .pqv-contact-item a { color: inherit; text-decoration: none; border-bottom: 1px solid rgba(200,169,110,0.4); }
        .pqv-contact-item a:hover { color: #3b82f6; border-color: #3b82f6; }

        .pqv-mobile-nav { display: none; }

        @media (max-width: 900px) {
          .pqv-sidebar { display: none; }
          .pqv-main { margin-left: 0; }

          .pqv-section { padding: 48px 20px; }
          .pqv-status-bar { padding: 12px 20px; font-size: 12px; }

          .pqv-display, .pqv-display-light {
            font-size: clamp(28px, 7vw, 42px);
            margin-bottom: 20px;
          }
          .pqv-body { font-size: 14px; }
          .pqv-eyebrow { font-size: 10px; }

          .pqv-doc-control { max-width: 100%; }
          .pqv-doc-control-row { grid-template-columns: 1fr; }
          .pqv-dc-label {
            border-right: none;
            border-bottom: 1px solid #e5e2da;
            padding: 8px 14px 4px;
          }
          .pqv-dc-value { padding: 4px 14px 10px; }

          .pqv-col-header {
            grid-template-columns: 1fr 80px;
            padding: 8px 14px;
          }
          .pqv-col-header span:nth-child(2),
          .pqv-col-header span:nth-child(3) { display: none; }
          .pqv-col-header span:nth-child(4) { display: block; }

          .pqv-line-item { grid-template-columns: 1fr 80px; }
          .pqv-li-cell:nth-child(2),
          .pqv-li-cell:nth-child(3) { display: none; }
          .pqv-li-cell { padding: 12px 14px; }

          .pqv-totals { padding: 12px 14px; }
          .pqv-total-row { gap: 24px; font-size: 13px; }
          .pqv-total-row span:last-child { min-width: 80px; }
          .pqv-total-row.grand { font-size: 15px; }

          .pqv-payment-block { margin-top: 24px; padding: 16px; }

          .pqv-sig-box { padding: 20px; }
          .pqv-sig-signer-grid { grid-template-columns: 1fr !important; }

          .pqv-signed-display { flex-direction: column; gap: 12px; }

          .pqv-contact-item { font-size: 14px; }

          .pqv-order-steps li { font-size: 13px; }

          .pqv-mobile-nav {
            display: flex;
            position: fixed;
            bottom: 0; left: 0; right: 0;
            background: white;
            border-top: 1px solid #e5e2da;
            z-index: 200;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .pqv-mobile-nav::-webkit-scrollbar { display: none; }
          .pqv-mobile-nav-item {
            flex-shrink: 0;
            padding: 12px 16px;
            font-family: "DM Sans", sans-serif;
            font-size: 12px;
            font-weight: 500;
            color: #6b6860;
            background: none;
            border: none;
            border-bottom: 2px solid transparent;
            cursor: pointer;
            white-space: nowrap;
            transition: color 0.15s, border-color 0.15s;
          }
          .pqv-mobile-nav-item.active {
            color: #3b82f6;
            border-bottom-color: #3b82f6;
            font-weight: 600;
          }

          .pqv-main { padding-bottom: 60px; }
        }

        @media (max-width: 480px) {
          .pqv-section { padding: 36px 16px; }
          .pqv-sig-box { padding: 16px; }
          .pqv-inv-card-header { padding: 12px 14px; font-size: 13px; }
        }

        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div className="pqv-root">
        <aside className="pqv-sidebar">
          <div className="pqv-logo-area">
            {quote.company?.logo ? (
              <img src={quote.company.logo} alt={quote.company.name} />
            ) : (
              <div className="pqv-logo-placeholder">
                <Building2 size={22} />
              </div>
            )}
            {quote.company?.registrationNumber && (
              <div className="pqv-company-sub">
                Company reg {quote.company.registrationNumber}
              </div>
            )}
          </div>

          <nav className="pqv-nav">
            {visibleSections.map((s) => (
              <button
                key={s.id}
                className={`pqv-nav-item${activeSection === s.id ? " active" : ""}`}
                onClick={() => scrollTo(s.id)}
              >
                {s.label}
              </button>
            ))}
          </nav>

          <div className="pqv-sidebar-actions">
            <button className="pqv-btn-icon" onClick={generatePDF}>
              <Download size={13} /> PDF
            </button>
            <button
              className="pqv-btn-icon"
              onClick={() => {
                navigator.clipboard?.writeText(window.location.href);
              }}
            >
              <Share2 size={13} /> Share
            </button>
          </div>

          {!isSigned && !isExpired && (
            <div style={{ padding: "0 16px 20px" }}>
              <button
                className="pqv-btn-sign"
                onClick={() => scrollTo("order")}
              >
                <Edit3 size={14} /> Sign Proposal
              </button>
            </div>
          )}
        </aside>

        <nav className="pqv-mobile-nav">
          {visibleSections.map((s) => (
            <button
              key={s.id}
              className={`pqv-mobile-nav-item${activeSection === s.id ? " active" : ""}`}
              onClick={() => scrollTo(s.id)}
            >
              {s.label}
            </button>
          ))}
          <button className="pqv-mobile-nav-item" onClick={generatePDF}>
            ⬇ PDF
          </button>
          {!isSigned && !isExpired && (
            <button
              className="pqv-mobile-nav-item"
              style={{ color: "#3b82f6", fontWeight: 600 }}
              onClick={() => scrollTo("order")}
            >
              ✍ Sign
            </button>
          )}
        </nav>

        <main className="pqv-main">
          {isSigned && (
            <div className="pqv-status-bar signed">
              <CheckCircle size={16} />
              This proposal was signed on {fmtDate(quote.signedAt)}
            </div>
          )}
          {isExpired && (
            <div className="pqv-status-bar expired">
              <Clock size={16} />
              This proposal has expired
            </div>
          )}

          <section
            id="introduction"
            ref={(el) => (sectionRefs.current.introduction = el)}
            className="pqv-section pqv-section-light"
          >
            <div className="pqv-eyebrow">Executive Summary</div>
            <h1 className="pqv-display">{quote.title}</h1>

            {quote.customerName && (
              <p className="pqv-body" style={{ marginBottom: 24 }}>
                Dear {quote.customerName},
              </p>
            )}

            {quote.description && (
              <p className="pqv-body">{quote.description}</p>
            )}

            <div className="pqv-doc-control">
              {[
                ["Company Name", quote.customerCompany || "—"],
                ["Company Contact", quote.customerName || "—"],
                ["Email", quote.customerEmail || "—"],
                ["Quote Reference", `#${quote.quoteNumber}`],
                ["Date Issued", fmtDate(quote.createdAt)],
                ...(quote.validUntil
                  ? [["Valid Until", fmtDate(quote.validUntil)]]
                  : []),
                ["Prepared By", quote.company?.name || "—"],
              ].map(([label, value]) => (
                <div key={label} className="pqv-doc-control-row">
                  <div className="pqv-dc-label">{label}:</div>
                  <div className="pqv-dc-value">{value}</div>
                </div>
              ))}
            </div>
          </section>

          {quote.aboutSection && (
            <section
              id="about"
              ref={(el) => (sectionRefs.current.about = el)}
              className="pqv-section pqv-section-cream"
            >
              <div className="pqv-eyebrow">
                Why {quote.company?.name || "Us"}
              </div>
              <h2 className="pqv-display">
                {quote.aboutSection.headline || `About ${quote.company?.name}`}
              </h2>

              {(quote.aboutSection.paragraphs || [quote.aboutSection.body])
                .filter(Boolean)
                .map((p, i) => (
                  <p key={i} className="pqv-body" style={{ marginBottom: 16 }}>
                    {p}
                  </p>
                ))}

              {quote.aboutSection.capabilities?.length > 0 && (
                <div style={{ marginTop: 40, maxWidth: 680 }}>
                  {quote.aboutSection.capabilities.map((cap, i) => (
                    <div key={i} className="pqv-scope-item">
                      <div className="pqv-scope-title">{cap.title}</div>
                      {cap.description && (
                        <div className="pqv-scope-desc">{cap.description}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          <section
            id="scope"
            ref={(el) => (sectionRefs.current.scope = el)}
            className="pqv-section pqv-section-light"
          >
            <div className="pqv-eyebrow">What's Included</div>
            <h2 className="pqv-display">Scope of Works</h2>

            {quote.scopeIntro && (
              <p className="pqv-body" style={{ marginBottom: 40 }}>
                {quote.scopeIntro}
              </p>
            )}

            <div style={{ maxWidth: 720 }}>
              {(quote.lineItems || []).map((li, i) => (
                <div key={li.id} className="pqv-scope-item">
                  <div className="pqv-scope-title">
                    {i + 1}. {li.name}
                  </div>
                  {li.description && (
                    <div className="pqv-scope-desc">{li.description}</div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      flexWrap: "wrap",
                      marginTop: li.description ? 6 : 4,
                    }}
                  >
                    {li.billingCycle &&
                      li.billingCycle !== "one_time" &&
                      li.billingCycle !== "" && (
                        <span className="pqv-li-badge">
                          {li.billingCycle.replace(/_/g, " ")}
                        </span>
                      )}
                    {li.termMonths && (
                      <span
                        className="pqv-li-badge"
                        style={{ background: "#eff6ff", color: "#1d4ed8" }}
                      >
                        {li.termMonths}-month term
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section
            id="investment"
            ref={(el) => (sectionRefs.current.investment = el)}
            className="pqv-section pqv-section-dark"
          >
            <div className="pqv-eyebrow" style={{ color: "#3b82f6" }}>
              Pricing Breakdown
            </div>
            <h2 className="pqv-display-light">Investment</h2>

            <div className="pqv-inv-card" style={{ marginTop: 32 }}>
              <div className="pqv-inv-card-header">{quote.title}</div>
              <div className="pqv-col-header">
                <span>Deliverable</span>
                <span>Qty</span>
                <span>Unit Price</span>
                <span>Total</span>
              </div>
              <div className="pqv-inv-card-body">
                {(quote.lineItems || []).map((li) => {
                  const qty = parseInt(li.quantity) || 1;
                  const unit = parseFloat(li.unitPrice) || 0;
                  const disc = parseFloat(li.discount) || 0;
                  const tax = parseFloat(li.taxRate) || 0;
                  const lineSub = qty * unit - disc;
                  const lineTotal =
                    parseFloat(li.totalPrice) || lineSub * (1 + tax / 100);
                  const isRecurring =
                    li.billingCycle &&
                    li.billingCycle !== "one_time" &&
                    li.billingCycle !== "";
                  return (
                    <div key={li.id} className="pqv-line-item">
                      <div className="pqv-li-cell">
                        <div className="pqv-li-name">{li.name}</div>
                        {li.description && (
                          <div className="pqv-li-desc">{li.description}</div>
                        )}
                        <div
                          style={{ display: "flex", gap: 4, flexWrap: "wrap" }}
                        >
                          {isRecurring && (
                            <span className="pqv-li-badge">
                              {li.billingCycle.replace(/_/g, " ")}
                            </span>
                          )}
                          {li.termMonths && (
                            <span className="pqv-li-badge">
                              {li.termMonths}-month term
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="pqv-li-cell pqv-li-num">{qty}</div>
                      <div className="pqv-li-cell pqv-li-num">
                        {fmt(unit, currency)}
                        {isRecurring && (
                          <div style={{ fontSize: 10, color: "#9e9b94" }}>
                            /{li.billingCycle.replace(/_/g, " ")}
                          </div>
                        )}
                      </div>
                      <div className="pqv-li-cell pqv-li-total">
                        {fmt(lineTotal, currency)}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="pqv-totals">
                {parseFloat(quote.subtotal) !==
                  parseFloat(quote.totalAmount) && (
                  <div className="pqv-total-row">
                    <span>Subtotal</span>
                    <span>{fmt(quote.subtotal, currency)}</span>
                  </div>
                )}
                {parseFloat(quote.discountAmount) > 0 && (
                  <div className="pqv-total-row" style={{ color: "#f97316" }}>
                    <span>
                      Discount{" "}
                      {quote.discountType === "percentage" &&
                        `(${quote.discountAmount}%)`}
                    </span>
                    <span>
                      −
                      {quote.discountType === "percentage"
                        ? `${quote.discountAmount}%`
                        : fmt(quote.discountAmount, currency)}
                    </span>
                  </div>
                )}
                {parseFloat(quote.taxAmount) > 0 && (
                  <div className="pqv-total-row">
                    <span>VAT / Tax</span>
                    <span>{fmt(quote.taxAmount, currency)}</span>
                  </div>
                )}
                <div className="pqv-total-row grand">
                  <span>Total</span>
                  <span>{fmt(quote.totalAmount, currency)}</span>
                </div>
              </div>
            </div>

            {parseFloat(quote.monthlyRecurring) > 0 && (
              <div
                style={{
                  marginTop: 16,
                  textAlign: "right",
                  fontSize: 13,
                  color: "#9e9b94",
                  maxWidth: 760,
                }}
              >
                Monthly Total:{" "}
                <strong style={{ color: "#f5f4f0" }}>
                  {fmt(quote.monthlyRecurring, currency)}/month
                </strong>
                {quote.taxRate > 0 &&
                  ` (${fmt(quote.monthlyRecurring * (1 + quote.taxRate / 100), currency)} inc VAT)`}
              </div>
            )}

            {quote.paymentTerms && (
              <div
                className="pqv-payment-block"
                style={{
                  background: "#2a2a2a",
                  border: "1px solid #3a3a3a",
                  borderLeft: "4px solid #3b82f6",
                  marginTop: 36,
                  maxWidth: 640,
                }}
              >
                <div className="pqv-payment-label">Payment Terms</div>
                <div className="pqv-payment-text" style={{ color: "#9e9b94" }}>
                  {quote.paymentTerms}
                </div>
              </div>
            )}
          </section>

          <section
            id="order"
            ref={(el) => (sectionRefs.current.order = el)}
            className="pqv-section pqv-section-light"
          >
            <div className="pqv-eyebrow">Getting Started</div>
            <h2 className="pqv-display">Order Form</h2>

            <p className="pqv-body">
              Let's get started — we have made our order process and onboarding
              as straightforward as possible. Please follow the steps below so
              we can get to work.
            </p>

            <ol className="pqv-order-steps">
              <li>
                Type your name in the signature box below and click "Accept and
                Sign".
              </li>
              <li>
                Our team will be in touch within 24 hours (excluding weekends)
                to initiate the onboarding process.
              </li>
              <li>
                Your account manager will schedule kick-off meetings and plan
                work deliverables.
              </li>
              <li>Financial arrangements will be confirmed and work begins.</li>
            </ol>

            {quote.company?.registrationText && (
              <p style={{ fontSize: 12.5, color: "#9e9b94", marginBottom: 0 }}>
                {quote.company.registrationText}
              </p>
            )}

            {isSigned && quote.signature ? (
              <div className="pqv-signed-display">
                <CheckCircle
                  size={24}
                  color="#166534"
                  style={{ flexShrink: 0, marginTop: 2 }}
                />
                <div>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 15,
                      color: "#166534",
                      marginBottom: 6,
                    }}
                  >
                    Signed by {quote.signature.signerName}
                    {quote.signature.signerTitle &&
                      ` — ${quote.signature.signerTitle}`}
                  </div>
                  <img
                    src={quote.signature.signatureImage}
                    alt="Signature"
                    className="pqv-signed-img"
                  />
                  <div style={{ fontSize: 12, color: "#6b6860", marginTop: 8 }}>
                    {quote.signature.signerEmail} ·{" "}
                    {fmtDate(quote.signature.signedAt)}
                  </div>
                  <button
                    className="pqv-btn-icon"
                    style={{ marginTop: 16, maxWidth: 200 }}
                    onClick={generatePDF}
                  >
                    <Download size={13} /> Download Signed Contract
                  </button>
                </div>
              </div>
            ) : !isExpired ? (
              <div className="pqv-sig-box">
                <p className="pqv-sig-consent">
                  I, {quote.customerName || "the undersigned"}, agree to the
                  terms of this agreement and I agree that my typed name below
                  can be used as a digital representation of my signature to
                  that fact.
                </p>

                <div
                  className="pqv-sig-signer-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                    marginBottom: 12,
                  }}
                >
                  <div>
                    <label className="pqv-sig-label">Full Name *</label>
                    <input
                      className="pqv-sig-input"
                      value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="pqv-sig-label">Email Address *</label>
                    <input
                      className="pqv-sig-input"
                      type="email"
                      value={signerEmail}
                      onChange={(e) => setSignerEmail(e.target.value)}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div style={{ marginBottom: 24 }}>
                  <label className="pqv-sig-label">Job Title</label>
                  <input
                    className="pqv-sig-input"
                    value={signerTitle}
                    onChange={(e) => setSignerTitle(e.target.value)}
                    placeholder="e.g. Managing Director"
                  />
                </div>

                <div className="pqv-sig-tabs">
                  <button
                    className={`pqv-sig-tab${signMode === "type" ? " active" : ""}`}
                    onClick={() => setSignMode("type")}
                  >
                    ✏ Sign by Typing
                  </button>
                  <button
                    className={`pqv-sig-tab${signMode === "draw" ? " active" : ""}`}
                    onClick={() => setSignMode("draw")}
                  >
                    ✍ Sign by Drawing
                  </button>
                </div>

                {signMode === "type" ? (
                  <div>
                    <label
                      className="pqv-sig-label"
                      style={{ marginBottom: 8, display: "block" }}
                    >
                      To accept and sign, type your name below
                    </label>
                    <input
                      className="pqv-sig-type-input"
                      value={typedSig}
                      onChange={(e) => setTypedSig(e.target.value)}
                      placeholder="Type your full name…"
                      style={{
                        marginBottom: 12,
                        width: "100%",
                        padding: "12px 14px",
                        background: "#2a2a2a",
                        border: "1px solid #3a3a3a",
                        borderRadius: 4,
                        color: "#f5f4f0",
                        fontFamily: "'DM Sans',sans-serif",
                        fontSize: 14,
                        outline: "none",
                      }}
                    />
                    {typedSig && (
                      <div className="pqv-sig-type-field">{typedSig}</div>
                    )}
                  </div>
                ) : (
                  <div>
                    <label
                      className="pqv-sig-label"
                      style={{ marginBottom: 8, display: "block" }}
                    >
                      Draw your signature below
                    </label>
                    <div className="pqv-sig-canvas-wrap">
                      <canvas
                        ref={(el) => {
                          canvasRef.current = el;
                          initCanvas(el);
                        }}
                        width={580}
                        height={130}
                        onMouseDown={startDraw}
                        onMouseMove={draw}
                        onMouseUp={stopDraw}
                        onMouseLeave={stopDraw}
                        onTouchStart={startDraw}
                        onTouchMove={draw}
                        onTouchEnd={stopDraw}
                      />
                    </div>
                    <button className="pqv-sig-clear" onClick={clearCanvas}>
                      Clear signature
                    </button>
                  </div>
                )}

                <label className="pqv-consent-row">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                  />
                  <span>
                    I agree that my electronic signature is the legal equivalent
                    of my manual signature and I accept the terms and conditions
                    of this proposal.
                  </span>
                </label>

                <button
                  className="pqv-accept-btn"
                  onClick={handleSign}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader
                        size={16}
                        style={{ animation: "spin 1s linear infinite" }}
                      />{" "}
                      Signing…
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} /> Accept and Sign
                    </>
                  )}
                </button>
              </div>
            ) : (
              <div
                style={{
                  marginTop: 32,
                  padding: "20px 24px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 4,
                  color: "#991b1b",
                  fontSize: 14,
                }}
              >
                This proposal has expired and can no longer be signed. Please
                contact us for a fresh proposal.
              </div>
            )}
          </section>

          {quote.termsAndConditions && (
            <section
              id="terms"
              ref={(el) => (sectionRefs.current.terms = el)}
              className="pqv-section pqv-section-dark"
            >
              <div className="pqv-eyebrow" style={{ color: "#3b82f6" }}>
                Legal
              </div>
              <h2 className="pqv-display-light">Terms &amp; Conditions</h2>
              <div
                className="pqv-terms-body"
                style={{ color: "#9e9b94", marginTop: 32 }}
              >
                {quote.termsAndConditions}
              </div>
            </section>
          )}

          {(quote.company?.email ||
            quote.company?.phone ||
            quote.company?.website) && (
            <section
              id="contact"
              ref={(el) => (sectionRefs.current.contact = el)}
              className="pqv-section pqv-section-dark"
              style={{ minHeight: "auto", textAlign: "center" }}
            >
              <div className="pqv-eyebrow" style={{ color: "#3b82f6" }}>
                Get In Touch
              </div>
              <h2 className="pqv-display-light" style={{ marginBottom: 40 }}>
                Contact Us
              </h2>

              <div
                style={{
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 0,
                }}
              >
                {quote.company?.email && (
                  <div className="pqv-contact-item">
                    <Mail size={16} color="#3b82f6" />
                    <a href={`mailto:${quote.company.email}`}>
                      {quote.company.email}
                    </a>
                  </div>
                )}
                {quote.company?.phone && (
                  <div className="pqv-contact-item">
                    <Phone size={16} color="#3b82f6" />
                    <a href={`tel:${quote.company.phone}`}>
                      {quote.company.phone}
                    </a>
                  </div>
                )}
                {quote.company?.website && (
                  <div className="pqv-contact-item">
                    <Globe size={16} color="#3b82f6" />
                    <a
                      href={quote.company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {quote.company.website.replace(/^https?:\/\//, "")}
                    </a>
                  </div>
                )}
                {quote.company?.address && (
                  <div className="pqv-contact-item">
                    <MapPin size={16} color="#3b82f6" />
                    <span>{quote.company.address}</span>
                  </div>
                )}
              </div>

              <div style={{ marginTop: 48, fontSize: 12, color: "#4a4744" }}>
                Viewed {quote.viewCount || 0} time
                {quote.viewCount !== 1 ? "s" : ""}
              </div>
            </section>
          )}
        </main>
      </div>
    </>
  );
};

const styles = {
  centreScreen: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f5f4f0",
    fontFamily: "'DM Sans', sans-serif",
  },
  errorCard: {
    background: "white",
    borderRadius: 8,
    padding: 48,
    maxWidth: 400,
    textAlign: "center",
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
  },
};

export default PublicQuoteView;
